import { capitalize, pluralize, uuid } from '@leland-dev/leland-ui-library';

import {
  type BootcampSession,
  type Class,
  DayOfWeek,
  type OneOnOneSession,
  RecurrenceType,
  type RecurringCadence,
  type Scalars,
} from '../__generated-gql-types__/globalTypes';

import phog from './analytics/posthog/posthog';
import { CLARITY_SESSION_ID_KEY } from './constants';

export const isSessionOneOnOne = <
  C extends Pick<Class, '__typename'>,
  O extends Pick<OneOnOneSession, '__typename'>,
  B extends Pick<BootcampSession, '__typename'>,
>(
  session: C | O | B | undefined,
): session is O => {
  return session?.__typename === 'OneOnOneSession';
};

export const isSessionClassOrFreeEvent = <
  C extends Pick<Class, '__typename'>,
  O extends Pick<OneOnOneSession, '__typename'>,
  B extends Pick<BootcampSession, '__typename'>,
>(
  session: C | O | B | undefined,
): session is C => {
  return session?.__typename === 'Class';
};

export const getSessionType = <
  C extends Pick<Class, '__typename' | 'isFreeEvent'>,
  O extends {
    __typename?: 'OneOnOneSession';
    introOrder?: { id: string } | null;
  },
  B extends Pick<BootcampSession, '__typename'>,
>(
  session: C | O | B | undefined,
): 'IntroCall' | 'OneOnOneSession' | 'FreeEvent' | 'Class' | 'Bootcamp' => {
  if (isSessionOneOnOne(session)) {
    return session.introOrder ? 'IntroCall' : 'OneOnOneSession';
  }
  if (session?.__typename === 'Class') {
    if (session.isFreeEvent) {
      return 'FreeEvent';
    }
    return 'Class';
  }
  return 'Bootcamp';
};

export const trackCoachingSessionScheduled = (props: {
  attendees: Array<Scalars['ApplicantUrn']['output']>;
  coachUrn: Scalars['CoachUrn']['output'];
  sessionUrn: Scalars['CoachingSessionUrn']['output'];
  startAt: number;
}) => {
  phog.capture('coaching_session_scheduled', {
    ...props,
  });
};

export const trackIntroCallScheduled = (props: {
  coachUrn: Scalars['CoachUrn']['output'];
  sessionUrn: Scalars['CoachingSessionUrn']['output'];
  startAt: number;
  createdAt: number;
}) => {
  phog.capture('intro_call_scheduled', {
    ...props,
  });
};

export const trackCoachingSessionAttended = (props: {
  coachUrn: Scalars['CoachUrn']['output'];
  sessionUrn: Scalars['CoachingSessionUrn']['output'];
  startAt: number;
  createdAt: number;
}) => {
  phog.capture('coaching_session_attended', {
    ...props,
  });
};

export const getSessionId = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  let sessionId = window.sessionStorage.getItem(CLARITY_SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = uuid();
    window.sessionStorage.setItem(CLARITY_SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

const getOrdinalSuffix = (n: number): string => {
  const base = n % 100;
  if (base >= 11 && base <= 13) {
    return 'th';
  }
  switch (base % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatOrdinal = (n: number): string => {
  return `${n}${getOrdinalSuffix(n)}`;
};

export const getDateInfo = (date: Date, durationMinutes: number) => {
  const startHour = date.getHours();
  const startMinute = date.getMinutes();

  const dayMapping: Record<number, DayOfWeek> = {
    0: DayOfWeek.SUNDAY,
    1: DayOfWeek.MONDAY,
    2: DayOfWeek.TUESDAY,
    3: DayOfWeek.WEDNESDAY,
    4: DayOfWeek.THURSDAY,
    5: DayOfWeek.FRIDAY,
    6: DayOfWeek.SATURDAY,
  };
  const dayOfWeek = dayMapping[date.getDay()];

  // Get week of month (1-5) by getting the week number of the day divided by 7 and rounding up
  const weekOfMonth = Math.ceil(date.getDate() / 7);

  return {
    startHour,
    startMinute,
    dayOfWeek,
    weekOfMonth,
    durationMinutes,
    weekdayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
    canBeLastWeekOfMonth: [4, 5].includes(weekOfMonth ?? 0),
  };
};

export const formatRecurringCadence = (
  recurringCadence: Pick<
    RecurringCadence,
    'dayOfWeek' | 'weekOfMonth' | 'type' | 'interval' | 'isLastWeekOfMonth'
  >,
) => {
  const { dayOfWeek, weekOfMonth } = recurringCadence;
  const weekdayName = capitalize(dayOfWeek?.toLowerCase() ?? '');

  switch (recurringCadence.type) {
    case RecurrenceType.DAILY:
      return 'every day';
    case RecurrenceType.WEEKLY:
      if (recurringCadence.interval && recurringCadence.interval > 1) {
        return `every ${pluralize(recurringCadence.interval, 'week')} on ${weekdayName}`;
      }
      return `every week on ${weekdayName}`;
    case RecurrenceType.MONTHLY:
      if (recurringCadence.isLastWeekOfMonth) {
        return `every month on the last ${weekdayName}`;
      }
      return `every month on the ${formatOrdinal(weekOfMonth ?? 0)} ${weekdayName}`;
  }
};
